import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Globe, Users, Calendar, MapPin, Flame } from "lucide-react";
import VisitorsMap from "@/pages/admin/VisitorsMap";

const fmtTs = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function VisitorsTab() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/visitors").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return <div className="pt-10 text-sm font-ui text-[#4A4A4A]">Loading visitor data…</div>;

  const cards = [
    { icon: Calendar, label: "Visits today", value: data.today, testid: "visitors-today" },
    { icon: Globe, label: "Total visits", value: data.total, testid: "visitors-total" },
    { icon: Users, label: "Unique IPs", value: data.unique_ips, testid: "visitors-unique" },
  ];

  return (
    <div data-testid="admin-visitors-tab">
      <AdminHeader testid="admin-header-visitors" eyebrow="Analytics" title="Website Visitors" description="Who is visiting the site — city, country and IP address per session." />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {cards.map(({ icon: Icon, label, value, testid }) => (
          <div key={testid} data-testid={testid} className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#C71E2E]/10 flex items-center justify-center shrink-0">
              <Icon size={17} className="text-[#C71E2E]" />
            </div>
            <div>
              <p className="font-ui text-2xl font-semibold text-[#1A1A1A]">{value}</p>
              <p className="text-[11px] font-ui tracking-[0.15em] uppercase text-[#4A4A4A]">{label}</p>
            </div>
          </div>
        ))}
      </div>

      <VisitorsMap visits={data.recent} />

      {data.top_countries.length > 0 && (
        <div data-testid="visitors-top-countries" className="mt-6 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <MapPin size={14} className="text-[#C71E2E]" /> Top Countries
          </p>
          <div className="mt-4 space-y-2.5">
            {data.top_countries.map((c) => (
              <div key={c.country} className="flex items-center gap-3">
                <span className="text-sm font-ui text-[#1A1A1A] w-40 truncate">{c.country}</span>
                <div className="flex-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
                  <div className="h-full rounded-full bg-[#C71E2E]" style={{ width: `${Math.round((c.count / data.top_countries[0].count) * 100)}%` }} />
                </div>
                <span className="text-sm font-ui text-[#4A4A4A] w-8 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.warm_leads?.length > 0 && (
        <div data-testid="visitors-warm-leads" className="mt-6 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6 border-l-4 border-[#C71E2E]">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <Flame size={14} className="text-[#C71E2E]" /> Warm Leads — 3+ visits, no account yet
          </p>
          <div className="mt-4 space-y-2">
            {data.warm_leads.map((w) => (
              <div key={w.ip} data-testid={`warm-lead-${w.ip}`} className="flex flex-wrap items-center justify-between gap-2 text-sm font-ui border-b border-[#F5EFE6] last:border-0 pb-2">
                <span className="text-[#1A1A1A] font-medium">{[w.city, w.country].filter(Boolean).join(", ") || "Unknown"}</span>
                <span className="font-mono text-xs text-[#4A4A4A]">{w.ip}</span>
                <span className="text-[#4A4A4A]">{w.sessions} visits · {w.pageviews} pageviews</span>
                <span className="text-[#4A4A4A] text-xs">last seen {fmtTs(w.last_seen)}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">These visitors keep coming back without booking — if one reaches out on WhatsApp, you know they're already interested.</p>
        </div>
      )}

      <div className="mt-6 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6 overflow-x-auto">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Recent Visits</p>
        <table className="mt-4 w-full text-sm font-ui">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[#4A4A4A] border-b border-[#E5DED5]">
              <th className="pb-2 pr-4">Time</th>
              <th className="pb-2 pr-4">City</th>
              <th className="pb-2 pr-4">Country</th>
              <th className="pb-2 pr-4">IP address</th>
              <th className="pb-2 pr-4">Landing page</th>
              <th className="pb-2 pr-4">Views</th>
              <th className="pb-2">Device</th>
            </tr>
          </thead>
          <tbody>
            {data.recent.map((v) => (
              <tr key={v.id} data-testid={`visitor-row-${v.id}`} className="border-b border-[#F5EFE6] last:border-0">
                <td className="py-2.5 pr-4 whitespace-nowrap text-[#4A4A4A]">{fmtTs(v.last_seen)}</td>
                <td className="py-2.5 pr-4 whitespace-nowrap text-[#1A1A1A]">{v.city ? `${v.city}${v.region ? ", " + v.region : ""}` : "—"}</td>
                <td className="py-2.5 pr-4 whitespace-nowrap text-[#1A1A1A]">{v.country || "Unknown"}</td>
                <td className="py-2.5 pr-4 whitespace-nowrap font-mono text-xs text-[#4A4A4A]">{v.ip}</td>
                <td className="py-2.5 pr-4 text-[#4A4A4A]">{v.path}</td>
                <td className="py-2.5 pr-4 text-[#4A4A4A]">{v.pageviews || 1}</td>
                <td className="py-2.5 text-[#4A4A4A] max-w-[220px] truncate" title={v.user_agent}>{/mobile|android|iphone/i.test(v.user_agent || "") ? "Mobile" : "Desktop"}</td>
              </tr>
            ))}
            {data.recent.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-[#4A4A4A]">No visits recorded yet — they appear here as people browse the site.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
